/**
 * Home-screen entry to Scratch & win. Shown only while the admin has it on:
 *  - card not scratched yet (or not drawn) → a loud "you have a card" strip;
 *  - scratched and WON, still valid → a quiet reminder of the reward;
 *  - scratched and blank / expired → nothing (the home screen is for prices).
 * Costs no reads beyond the two listeners the card screen also uses.
 */
import { useNavigate } from 'react-router-dom';
import { useMyScratchCard, useScratchConfig } from '../../hooks/useScratchCard';
import { ROUTES } from '../../routes/paths';

const dateFmt = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' });

export function ScratchBanner(): JSX.Element | null {
  const navigate = useNavigate();
  const { config } = useScratchConfig();
  const { card, loading } = useMyScratchCard(config);

  if (!config?.enabled || loading) return null;

  if (card?.revealedAt) {
    if (!card.won || card.redeemedAt) return null;
    if (card.expiresAt && card.expiresAt < Date.now()) return null;
    return (
      <button
        type="button"
        onClick={() => navigate(ROUTES.scratch)}
        className="mx-4 mb-1 mt-1 flex items-center gap-2.5 rounded-xl bg-emerald-50 px-3.5 py-2.5 text-left ring-1 ring-emerald-100"
      >
        <span className="text-lg" aria-hidden>
          🎁
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12.5px] font-bold text-drop">
            Your reward: {card.prizeTitle}
          </span>
          <span className="block text-[11px] text-emerald-700/80">
            {card.expiresAt ? `Valid till ${dateFmt.format(new Date(card.expiresAt))} · ` : ''}
            Tap to see how to claim
          </span>
        </span>
        <span className="text-emerald-700/60" aria-hidden>
          ›
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate(ROUTES.scratch)}
      className="mx-4 mb-1 mt-1 flex items-center gap-3 rounded-xl bg-gradient-to-r from-brand to-brand-dark px-4 py-3 text-left text-white shadow-mini"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/15 text-xl" aria-hidden>
        🎟️
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-extrabold leading-tight">
          {config.title || 'Scratch & Win'} — a card is waiting for you
        </span>
        <span className="block text-[11.5px] text-white/80">
          Cashback and HP accessories to be won. Tap to scratch
        </span>
      </span>
      <span className="rounded-full bg-white px-3 py-1 text-[11px] font-extrabold text-brand-dark">
        Scratch
      </span>
    </button>
  );
}
